import { getCoSyncDB } from '@/renderer/scripts/CoSync/SyncCenter/Repo/CoSyncDB';
import CoSyncInfoDAO from '@/renderer/scripts/CoSync/SyncCenter/Repo/CoSyncInfoDAO';
import { UniqueIdGenerator } from '@/renderer/scripts/CoSync/SyncCenter/Repo/UniqueIdGenerator';
import { CoSyncInfo, CoSyncTaskConfig } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';

/**
 * @description 同步盘的全局数据仓库
 * @export
 * @class SyncGlobalRepo
 */
export default class SyncGlobalRepo {
  private serviceId2Config: Map<number, CoSyncInfo>;
  private configDao: CoSyncInfoDAO;
  private uniqueIdGenerator: UniqueIdGenerator;
  private whenReady: Promise<void>;

  constructor(userInfo: CoFile.UserInfo) {
    this.serviceId2Config = new Map<number, CoSyncInfo>();
    this.configDao = new CoSyncInfoDAO(userInfo, getCoSyncDB());
    this.uniqueIdGenerator = new UniqueIdGenerator();
    let readyReslover;
    this.whenReady = new Promise(resolve => (readyReslover = resolve));

    this.asyncSetup(readyReslover);
  }

  /**
   * @description 获取所有配置
   * @return {*}  {Promise<CoSyncInfo[]>}
   * @memberof SyncGlobalRepo
   */
  public async getAllConfig(): Promise<CoSyncInfo[]> {
    await this.whenReady;

    return Array.from(this.serviceId2Config.values());
  }

  /**
   * @description 添加配置
   * @param {CoSyncTaskConfig} taskConfig
   * @param {number} remoteSyncId
   * @return {*}  {CoSyncInfo}
   * @memberof SyncGlobalRepo
   */
  public addConfig(taskConfig: CoSyncTaskConfig, remoteSyncId: number): CoSyncInfo {
    const localSyncId = this.uniqueIdGenerator.generateId();
    const config: CoSyncInfo = {
      syncId: remoteSyncId,
      localSyncId,
      config: taskConfig,
    };
    this.serviceId2Config.set(localSyncId, config);

    this.configDao.add(config);

    return config;
  }

  /**
   * @description 更新配置
   * @param {CoSyncInfo} info
   * @return {*}
   * @memberof SyncGlobalRepo
   */
  public updateConfig(info: CoSyncInfo) {
    this.serviceId2Config.set(info.localSyncId, info);
    return this.configDao.update(info);
  }

  /**
   * @description 移除配置
   * @param {number} localSyncId
   * @return {*}  {Promise<void>}
   * @memberof SyncGlobalRepo
   */
  public async removeConfig(localSyncId: number, isPermanent: boolean): Promise<void> {
    this.serviceId2Config.delete(localSyncId);

    if (isPermanent) {
      this.uniqueIdGenerator.deprecateId(localSyncId);
      await this.configDao.remove(localSyncId);
    }
  }

  /**
   * @description 异步初始化
   * @private
   * @param {*} readyReslover
   * @memberof SyncGlobalRepo
   */
  private async asyncSetup(readyReslover: any) {
    const allLocalSyncId = await (await this.configDao.getAllConfigFromAllUser()).map(it => it.localSyncId);
    this.uniqueIdGenerator.reset(allLocalSyncId);

    const configWithIdList = await this.configDao.getAllConfigFromCurrentUser();
    configWithIdList.forEach(it => this.serviceId2Config.set(it.localSyncId, it));
    readyReslover();
  }
}
